import React, { useEffect, useState } from 'react';
import { patientService } from '../services/patientService';
import { request } from '../../../lib/api';
import EmptyState from '../../../components/ui/EmptyState';
import Spinner from '../../../components/ui/Spinner';
import ConfirmModal from '../../../components/common/ConfirmModal';

interface PatientInsurancesTabProps {
  patientId: number;
}

interface PatientInsurance {
  id: number;
  policy_number: string | null;
  nss: string | null;
  card_number: string | null;
  insurance_company_plan?: {
    company?: { name: string };
    plan?: { name: string };
  };
}

interface InsuranceCatalogPlan {
  id: number;
  name: string;
  insurance_company_id: number;
  company_name: string;
}

const emptyForm = {
  insurance_company_plan_id: 0,
  policy_number: '',
  nss: '',
  card_number: '',
};

const PatientInsurancesTab: React.FC<PatientInsurancesTabProps> = ({ patientId }) => {
  const [catalogPlans, setCatalogPlans] = useState<InsuranceCatalogPlan[]>([]);
  const [insurances, setInsurances] = useState<PatientInsurance[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchInsurances = async () => {
    try {
      setLoading(true);
      const data = await patientService.getInsurances(patientId);
      setInsurances(data);
    } catch (err) {
      console.error('Error al cargar seguros:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInsurances();
  }, [patientId]);

  useEffect(() => {
    request('/api/v1/catalogs/insurance-plans')
      .then((res) => setCatalogPlans(res.data || []))
      .catch((err) => console.error('Error al cargar catálogo de planes:', err));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.insurance_company_plan_id) {
      setError('El plan de seguro es obligatorio');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await patientService.addInsurance(patientId, form);
      setForm(emptyForm);
      setShowForm(false);
      await fetchInsurances();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al guardar el seguro');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (pendingDeleteId === null) return;
    setDeleting(true);
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    try {
      await patientService.deleteInsurance(patientId, id);
      setInsurances(prev => prev.filter(i => i.id !== id));
    } catch (err) {
      console.error('Error al eliminar seguro:', err);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <Spinner variant="full-page" text="Cargando seguros..." size="md" />;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Seguros ({insurances.length})</h5>
        <button
          className="btn btn-sm btn-primary"
          onClick={() => { setShowForm(!showForm); setError(null); }}
        >
          <i className={`fa ${showForm ? 'fa-times' : 'fa-plus'}`}></i>
          {showForm ? ' Cancelar' : ' Agregar Seguro'}
        </button>
      </div>

      {showForm && (
        <div className="card mb-3" style={{ padding: 15, backgroundColor: '#f9f9f9', border: '1px solid #ddd', borderRadius: 4 }}>
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-4">
                <div className="form-group">
                  <label>Plan *</label>
                  <select
                    className="form-control"
                    value={form.insurance_company_plan_id}
                    onChange={(e) => setForm(prev => ({ ...prev, insurance_company_plan_id: parseInt(e.target.value) || 0 }))}
                    required
                  >
                    <option value={0}>Seleccionar...</option>
                    {catalogPlans.map(p => (
                      <option key={p.id} value={p.id}>{p.company_name} — {p.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-3">
                <div className="form-group">
                  <label>Póliza</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.policy_number}
                    onChange={(e) => setForm(prev => ({ ...prev, policy_number: e.target.value }))}
                  />
                </div>
              </div>
              <div className="col-md-3">
                <div className="form-group">
                  <label>NSS</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.nss}
                    onChange={(e) => setForm(prev => ({ ...prev, nss: e.target.value }))}
                  />
                </div>
              </div>
              <div className="col-md-2">
                <div className="form-group">
                  <label>Carnet</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.card_number}
                    onChange={(e) => setForm(prev => ({ ...prev, card_number: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            {error && (
              <div className="alert alert-danger alert-sm" style={{ padding: '8px 12px', marginBottom: 10 }}>
                {error}
              </div>
            )}
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
              {saving ? <Spinner size="sm" text="Guardando..." /> : (<><i className="fa fa-save"></i> Guardar</>)}
            </button>
          </form>
        </div>
      )}

      {insurances.length === 0 ? (
        <EmptyState icon="fa-id-card" title="No tiene seguros registrados" className="py-4" />
      ) : (
        <table className="table table-striped">
          <thead>
            <tr>
              <th>Aseguradora</th>
              <th>Plan</th>
              <th>Póliza</th>
              <th>NSS</th>
              <th>Carnet</th>
              <th style={{ width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {insurances.map((insurance) => (
              <tr key={insurance.id}>
                <td>{insurance.insurance_company_plan?.company?.name || '-'}</td>
                <td>{insurance.insurance_company_plan?.plan?.name || '-'}</td>
                <td>{insurance.policy_number || '-'}</td>
                <td>{insurance.nss || '-'}</td>
                <td>{insurance.card_number || '-'}</td>
                <td>
                  <button
                    className="btn btn-xs bg-danger-light"
                    title="Eliminar"
                    onClick={() => setPendingDeleteId(insurance.id)}
                  >
                    <i className="fa fa-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ConfirmModal
        show={pendingDeleteId !== null}
        title="Eliminar seguro"
        body="¿Está seguro que desea eliminar este seguro? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        confirmVariant="danger"
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => { if (!deleting) setPendingDeleteId(null); }}
      />
    </div>
  );
};

export default PatientInsurancesTab;
